import { IDeskActivityOverviewStats } from '@kcftech/prometheustypescriptapiclient';
import { getPopupMaxHeight } from 'components/dropdown/DropdownPopup';
import { usePermissions } from 'contexts/PermissionsContext';
import { usePopup } from 'custom_hooks/usePopup';
import { useWindowSize } from 'custom_hooks/useWindowSize';
import {
    IssueFaultType,
    useFaultTypes,
} from 'pages/Desk/issues/fault_type/useFaultTypes';
import { useMemo } from 'react';
import { BadgeWrapper } from 'shared/badge_icon/BadgeWrapper';
import { TreeNode } from 'shared/data_tree/types';
import { FaultTypeQuickFilterPopup } from 'shared/filter_pane/FaultTypeQuickFilter';
import { QuickFilterToggle } from 'shared/filter_pane/QuickFilterToggle';
import styled from 'styled-components';
import { borderColor, useThemeValues } from 'styles/theme';

const StyledQuickFilterToggle = styled(QuickFilterToggle)<{
    $isOpen: boolean;
    isToggled: boolean;
}>`
    background-color: ${({ $isOpen, isToggled }) =>
        $isOpen && !isToggled ? borderColor : ''} !important;
`;

export type FaultTypesFilterButtonProps = {
    faultTypeIds: string[];
    onChangeFaultTypeIds: (faultTypeIds: string[]) => void;
    availableFaultTypes: IDeskActivityOverviewStats['FaultTypes'] | undefined;
};

export function FaultTypesFilterButton({
    faultTypeIds,
    onChangeFaultTypeIds,
    availableFaultTypes,
}: FaultTypesFilterButtonProps) {
    useWindowSize(true);

    const { primaryText, secondaryButton } = useThemeValues();
    const { isOpen, close, toggleOpen, triggerRef, popupRef } = usePopup({
        closeOnClickOutside: true,
    });

    const {
        issue: { canViewInternalIssues },
    } = usePermissions();

    const { faultTypesTree } = useFaultTypes({
        mode: 'machineHealth',
        isInternalOnly: canViewInternalIssues ? undefined : canViewInternalIssues,
    });

    // Filter the tree to only include fault types that exist in the available
    // data
    const filteredFaultTypesTree = useMemo(() => {
        if (!faultTypesTree || !availableFaultTypes) {
            return faultTypesTree;
        }

        const availableFaultTypeIds = new Set(
            availableFaultTypes.map((ft) => ft.FaultTypeId)
        );

        // Get all nodes that should be kept:
        // 1. Nodes that are in the available fault types
        // 2. Parent nodes of available fault types (to maintain tree structure)
        const idsToKeep = new Set<string>();
        for (const item of faultTypesTree.allNodes) {
            if (!availableFaultTypeIds.has(item.id)) {
                continue;
            }
            idsToKeep.add(item.id);
            let parentItem: TreeNode<IssueFaultType> | undefined = item;
            while ((parentItem = parentItem.parent)) {
                if (idsToKeep.has(parentItem.id)) {
                    break;
                }
                idsToKeep.add(parentItem.id);
            }
        }

        const nodeIdsToRemove = faultTypesTree.allNodes
            .filter((node) => !idsToKeep.has(node.id))
            .map((node) => node.id);

        return faultTypesTree.remove(nodeIdsToRemove);
    }, [faultTypesTree, availableFaultTypes]);

    const adjustedMaxHeight = getPopupMaxHeight(
        triggerRef.current,
        window.innerHeight,
        false
    );

    const maxHeight = `max(8rem, min(50rem, ${adjustedMaxHeight}))`;

    return (
        <>
            <BadgeWrapper
                number={faultTypeIds.length}
                textColor={primaryText}
                position={{
                    corner: 'topright',
                    inset: '-8px',
                }}
                backgroundColor={secondaryButton}
                badgeHidden={faultTypeIds.length === 0}
            >
                <StyledQuickFilterToggle
                    ref={triggerRef}
                    id="fault-types-filter"
                    isToggled={faultTypeIds.length > 0}
                    onToggle={toggleOpen}
                    $isOpen={isOpen}
                    onBlur={(e) => {
                        if (
                            e.relatedTarget &&
                            e.relatedTarget !== triggerRef.current &&
                            !popupRef.current?.contains(e.relatedTarget)
                        ) {
                            close();
                        }
                    }}
                >
                    Fault Types
                </StyledQuickFilterToggle>
            </BadgeWrapper>
            {isOpen && filteredFaultTypesTree && (
                <FaultTypeQuickFilterPopup
                    close={close}
                    maxHeight={maxHeight}
                    selectedFaultTypeIds={faultTypeIds}
                    onChangeFaultTypeIds={onChangeFaultTypeIds}
                    popupRef={popupRef}
                    triggerRef={triggerRef}
                    faultTypesTree={filteredFaultTypesTree}
                    issueMode="machineHealth"
                />
            )}
        </>
    );
}
